import React, { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, Truck, ShoppingCart, Receipt, Boxes, TrendingUp,
  Landmark, ArrowLeftRight, Scale, Store as StoreIcon, UserSquare, LogOut, Package,
  Shield, ClipboardList, MessageSquareWarning, PieChart, ChevronsLeft, ChevronsRight, PanelLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const LOGO_URL = "https://customer-assets-agu9un31.emergentagent.net/job_hotto-inventory/artifacts/1jsxg7bu_logo%20hotto%20cianjur.jpeg";
const IDLE_MS = 10 * 60 * 1000;

const ROLE_LABELS = {
  owner: "Owner", staff: "Staff", kasir: "Kasir", mitra: "Mitra",
  manager_ops: "Manager Ops", finance: "Finance", investor: "Investor",
};

// Kategori sidebar - Portal Mitra Owner-View + Pengumuman + Investor jadi 1 grup "Portal Umum" untuk owner
const sections = [
  {
    key: "utama", label: "Utama",
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, allow: ["owner", "manager_ops", "finance", "kasir", "staff"] },
      { to: "/purchases", label: "Pembelian dari Distributor", icon: Truck, allow: ["owner", "manager_ops"] },
    ],
  },
  {
    key: "direct", label: "Direct Selling",
    items: [
      { to: "/pos", label: "Kasir Penjualan Langsung", icon: ShoppingCart, allow: ["owner", "kasir", "staff", "manager_ops"] },
      { to: "/sales", label: "Riwayat Penjualan", icon: Receipt, allow: ["owner", "kasir", "staff", "manager_ops", "finance"] },
    ],
  },
  {
    key: "retail", label: "Retail",
    items: [
      { to: "/admin/mitra", label: "Portal Mitra (View)", icon: PieChart, allow: ["owner", "finance", "manager_ops", "investor"] },
      { to: "/admin/opname-review", label: "Review Opname", icon: ClipboardList, allow: ["owner"] },
      { to: "/distributions", label: "Penyaluran Retail", icon: Boxes, allow: ["owner", "manager_ops"] },
      { to: "/store-sales", label: "Laporan Penjualan Retail", icon: Receipt, allow: ["owner", "manager_ops", "finance"] },
      { to: "/store-sales-history", label: "Riwayat Penjualan Retail", icon: ClipboardList, allow: ["owner", "manager_ops", "finance"] },
      { to: "/admin/orders", label: "Pemesanan Masuk", icon: ShoppingCart, allow: ["owner", "manager_ops"] },
    ],
  },
  {
    key: "keuangan", label: "Keuangan",
    items: [
      { to: "/modal", label: "Modal & Kewajiban", icon: Landmark, allow: ["owner", "finance"] },
      { to: "/cashflow", label: "Arus Kas", icon: ArrowLeftRight, allow: ["owner", "finance"] },
      { to: "/balance-sheet", label: "Neraca", icon: Scale, allow: ["owner", "finance"] },
      { to: "/reports", label: "Laporan Laba", icon: TrendingUp, allow: ["owner", "finance"] },
      { to: "/expenses", label: "Pengeluaran", icon: Receipt, allow: ["owner", "finance", "manager_ops"] },
    ],
  },
  {
    key: "master", label: "Master Data",
    items: [
      { to: "/products", label: "Produk & Stok", icon: Package, allow: ["owner", "manager_ops", "finance"] },
      { to: "/distributors", label: "Distributor", icon: Truck, allow: ["owner", "manager_ops"] },
      { to: "/stores", label: "Toko Binaan / Retail", icon: StoreIcon, allow: ["owner", "manager_ops"] },
      { to: "/customers", label: "Konsumen", icon: UserSquare, allow: ["owner", "kasir", "manager_ops"] },
    ],
  },
  {
    key: "portal_mitra", label: "Portal Mitra",
    items: [
      { to: "/mitra", label: "Stok Opname & Keluhan", icon: ClipboardList, allow: ["mitra"] },
      { to: "/mitra/orders", label: "Pemesanan Stok", icon: ShoppingCart, allow: ["mitra"] },
      { to: "/mitra/history", label: "Riwayat Opname", icon: ClipboardList, allow: ["mitra"] },
      { to: "/mitra/identitas", label: "Identitas Retail", icon: UserSquare, allow: ["mitra"] },
    ],
  },
  // === Portal Umum (owner-centric): Pengumuman + Portal Mitra View + Investor ===
  {
    key: "portal_umum", label: "Portal & Info",
    items: [
      { to: "/announcements", label: "Pengumuman", icon: MessageSquareWarning, allow: ["owner", "mitra", "manager_ops", "finance", "kasir", "investor", "staff"] },
      { to: "/investor", label: "Investor", icon: TrendingUp, allow: ["owner", "investor"] },
    ],
  },
  {
    key: "super_admin", label: "Super Admin",
    items: [
      { to: "/super-admin", label: "Super Admin", icon: Shield, allow: ["owner"] },
    ],
  },
];

export default function Layout({ children }) {
  const { user, logout, role } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(
    () => window.innerWidth < 768 || localStorage.getItem("hotto_sidebar_collapsed") === "1",
  );
  const idleRef = useRef(null);

  const doLogout = async () => { await logout(); navigate("/login"); };
  const canSee = (allow) => !allow || allow.includes(role);
  const toggle = () => {
    setCollapsed((c) => {
      const nv = !c;
      localStorage.setItem("hotto_sidebar_collapsed", nv ? "1" : "0");
      return nv;
    });
  };

  // Auto-collapse setelah 30 detik idle
  useEffect(() => {
    const resetIdle = () => {
      if (idleRef.current) clearTimeout(idleRef.current);
      idleRef.current = setTimeout(() => {
        setCollapsed(true);
        localStorage.setItem("hotto_sidebar_collapsed", "1");
      }, IDLE_MS);
    };
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];
    events.forEach((e) => window.addEventListener(e, resetIdle, { passive: true }));
    resetIdle();
    return () => {
      events.forEach((e) => window.removeEventListener(e, resetIdle));
      if (idleRef.current) clearTimeout(idleRef.current);
    };
  }, []);

  // Kalau user aktif klik navigation, expand kembali
  useEffect(() => {
    if (collapsed) {
      // do nothing, user chose collapse
    }
    // eslint-disable-next-line
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex bg-[var(--hotto-bg)]">
      <aside
        className={`${collapsed ? "w-0 md:w-16" : "w-64"} fixed md:sticky z-40 top-0 left-0
          transition-[width] duration-300 bg-white border-r border-[var(--hotto-border)] flex
          flex-col h-screen relative overflow-hidden`}
      >
        <div className={`px-3 py-4 border-b border-[var(--hotto-border)] flex items-center ${collapsed ? "justify-center" : "gap-3 px-6 py-5"}`}>
          <img src={LOGO_URL} alt="H" className={`${collapsed ? "w-9 h-9" : "w-11 h-11"} rounded-full ring-2 ring-emerald-100 shadow-sm object-contain bg-white shrink-0`} />
          {!collapsed && (
            <div className="min-w-0">
              <div className="font-bold text-[15px] leading-tight truncate" style={{fontFamily:'Outfit'}}>Hotto Branch Cianjur</div>
              <div className="text-[11px] text-emerald-700 italic font-medium mt-0.5">Gut Bless You</div>
            </div>
          )}
        </div>

        {/* Toggle button */}
        <button
          onClick={toggle}
          data-testid="btn-toggle-sidebar"
          className="absolute -right-3 top-16 z-20 w-6 h-6 rounded-full bg-white border border-slate-300 shadow flex items-center justify-center hover:bg-slate-50 transition-colors"
          title={collapsed ? "Buka sidebar" : "Ciutkan sidebar"}
        >
          {collapsed ? <ChevronsRight className="w-3 h-3"/> : <ChevronsLeft className="w-3 h-3"/>}
        </button>

        <nav className="flex-1 py-2 overflow-y-auto overflow-x-hidden">
          {sections.map((sec) => {
            const visible = sec.items
              .filter((i) => canSee(i.allow))
              .slice()
              .sort((a, b) => a.label.localeCompare(b.label, "id", { sensitivity: "base" }));
            if (visible.length === 0) return null;
            return (
              <div key={sec.key} className="mb-2" data-testid={`nav-section-${sec.key}`}>
                {!collapsed && <div className="px-6 mt-2 mb-1 overline">{sec.label}</div>}
                {collapsed && <div className="mx-3 my-2 border-t border-slate-100"></div>}
                <div className="space-y-0.5">
                  {visible.map((item) => (
                    <NavLink
                      key={item.to} to={item.to} end={item.end}
                      title={collapsed ? item.label : undefined}
                      data-testid={`nav-${item.to.replace("/", "") || "dashboard"}`}
                      className={({ isActive }) =>
                        `side-link flex items-center gap-3 ${collapsed ? "justify-center px-3 py-2.5 mx-2 rounded-md" : "px-6 py-2"} text-sm ${isActive ? "active font-medium" : "text-slate-700"}`
                      }
                    >
                      <item.icon className="w-4 h-4 shrink-0"/>
                      {!collapsed && <span className="truncate">{item.label}</span>}
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>

        <div className={`border-t border-[var(--hotto-border)] ${collapsed ? "px-2 py-3" : "px-6 py-4"}`}>
          {!collapsed && (
            <>
              <div className="text-xs text-[var(--hotto-muted)] mb-1">Masuk sebagai</div>
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-xs text-[var(--hotto-muted)] mb-3">{ROLE_LABELS[role] || role} · {user?.username || user?.email}</div>
            </>
          )}
          <Button variant="outline" size="sm" onClick={doLogout} data-testid="btn-logout" className={collapsed ? "w-full px-0" : "w-full"} title="Keluar">
            <LogOut className="w-4 h-4"/> {!collapsed && <span className="ml-2">Keluar</span>}
          </Button>
        </div>
      </aside>
      {collapsed && (
        <button
          type="button"
          onClick={toggle}
          data-testid="btn-open-mobile-sidebar"
          className="fixed left-3 top-3 z-30 flex h-10 w-10 items-center justify-center rounded-md
            border border-[var(--hotto-border)] bg-white text-slate-700 shadow-sm md:hidden"
          title="Buka navigasi"
        >
          <PanelLeft className="h-4 w-4" />
        </button>
      )}

      <main className="flex-1 min-w-0">
        <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px]">{children}</div>
      </main>
    </div>
  );
}
